import React, {useState, useEffect, useCallback,useMemo} from "react";
import {useDispatch, useSelector} from 'react-redux';
import PageBase from "../../../components/PageBase";
import {useLocation, useNavigate} from 'react-router-dom';
import * as ROUTES from "../../../utils/routes.js";
import Datatable from "../../../components/Datatable";
import {getLabelByField as getMarketingLabelByField} from "../../../utils/fields/marketing.js";
import {getLabelByField as getFundLabelByField} from "../../../utils/fields/funds.js";
import {getLabelByField as getHeatmapLabelByField} from "../../../utils/fields/heatmaps.js";
import {getLabelByField as getOpportunityLabelByField} from "../../../utils/fields/opportunities.js";
import { getDashboardDisplayFields as getOpportunityDashboardFields, } from "../../../utils/fields/opportunities";
import { getDashboardDisplayFields as getMarketingDashboardFields} from "../../../utils/fields/marketing";
import { getDashboardDisplayFields as getFundDashboardFields } from "../../../utils/fields/funds";
import { getDashboardDisplayFields as getHeatmapDashboardFields} from "../../../utils/fields/heatmaps";
import {applyFilter, isNullOrEmptyArray} from "../../../utils/general.js";
import availableFilters from "../../Opportunities/availableFilters";
import Filters from "../../../components/Filters";
import EditOpportunity from "../../Opportunities/EditOpportunity";
import PopupWin from "../../../components/PopupWin";
import moment from "moment";
import usePersistentFilters from '../../../hooks/usePersistentFilters';
import useColumnFilters from '../../../hooks/useColumnFilters';
import constants from "../../../utils/constants.js";
import ListManager from '../../../components/ListManager';
import {createColumnConfigs} from '../../../utils/fieldUtils.js';

import './DashboardReport.scss';
import * as opportunityActions from "../../../redux/Opportunity/actions";
import { listClasses } from "@mui/material";

const getDashboardFieldsByObject = (objectName,isDefault=false, keys = null) => {
	switch(objectName) {
		case "opportunities": return getOpportunityDashboardFields(isDefault,keys);
		case "marketing": return getMarketingDashboardFields(isDefault,keys);
		case "funds": return getFundDashboardFields(isDefault,keys);
		case "heatmaps": return getHeatmapDashboardFields(isDefault,keys);
		default: return [];
	}
};

const DashboardReport = props => {
	const [editOpportunity, setEditOpportunity] = useState(null);
	const [listColumns, setListColumns] = useState([]);

	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const {title, data, selectedObject} = location.state;

	// Initialize data processing
	const updatedData = React.useMemo(() => prepareObjects(data, selectedObject), [data, selectedObject]);
	const headers = React.useMemo(() => 
		updatedData && !isNullOrEmptyArray(updatedData) ? Object.keys(updatedData[0]) : [],
		[updatedData]
	);
	const fieldsToUse = useMemo(() => {
	return listColumns.length > 0
		? createColumnConfigs(getDashboardFieldsByObject(selectedObject, false, listColumns))
		: createColumnConfigs(getDashboardFieldsByObject(selectedObject, true));
	}, [listColumns, selectedObject]);


	// Initialize filters
	const {
		selectedFilters,
		setSelectedFilters,
		clearFilters: clearPersistentFilters
	} = usePersistentFilters(`dashboard-${title}`, '', []);
	
	const filteredAllData = React.useMemo(() => {
		if (selectedObject === 'opportunities') {
			let filtered = [...updatedData];
			selectedFilters.forEach(filter => {
				filtered = applyFilter(filtered, filter);
			});
			return filtered;
		}
		return updatedData;
	}, [updatedData, selectedFilters, selectedObject]);

	// Initialize column filters
	const {
		filters,
		sortBy,
		sortOrder,
		filteredData,
		handleFilterChange,
		resetWith,
		updateData,
		uniqueValuesByColumn,
		filterOrder
	} = useColumnFilters({
		data: filteredAllData || [],
		columns: fieldsToUse,
		storageKey: `dashboard-${title}`,
		initialState: {}
	});

	// Event handlers
	const onRowClick = useCallback(async (row) => {
	const original = row._original || row;
	if (selectedObject === 'opportunities') {
		await dispatch(opportunityActions.loadOpportunityById(original.Id, opportunityLoaded));
	} else {
		navigate(`/${getPathByObject(selectedObject)}/${original.Id}`);
	}
	}, [selectedObject, dispatch, navigate]);

	const opportunityLoaded = useCallback((opp) => {
		setEditOpportunity(opp);
	}, []);

	const onOpportunitySaved = useCallback(() => {
		setEditOpportunity(null);
	}, []);

	const addFilter = useCallback((filter) => {
		setSelectedFilters(prevFilters => {
			const index = prevFilters.findIndex(f => f.name === filter.name);
			if(index < 0) {
				return [...prevFilters, filter];
			}
			const updated = [...prevFilters];
			updated[index] = filter;
			return updated;
		});
	}, [setSelectedFilters]);

	const removeFilter = useCallback((filterToRemove) => {
		setSelectedFilters(prevFilters => prevFilters.filter(f => f.name !== filterToRemove.name));
	}, [setSelectedFilters]);

	const onClearFilters = useCallback(() => {
		clearPersistentFilters();
	}, [clearPersistentFilters]);

	// Update data when it changes
	useEffect(() => {
		if (updatedData && !isNullOrEmptyArray(updatedData)) {
			updateData(updatedData);
		}
	}, [updatedData, updateData]);

	// Process data with filters
	const processedData = React.useMemo(() => {
		let processed = [...filteredData];

		if(selectedObject === 'opportunities') {
			selectedFilters.forEach(filter => {
				processed = applyFilter(processed, filter);
			});
		}

		return processed.map(item => {
			const newItem = { ...item };
			if (newItem['Last_Stage_Change_Date_internal__c']) {
				newItem['Last_Stage_Change_Date_internal__c'] = moment(newItem['Last_Stage_Change_Date_internal__c']).format('DD/MM/YYYY');
			}
			if (newItem.start_date) {
				newItem.start_date = moment(newItem.start_date).format('DD/MM/YYYY');
			}
			return newItem;
		});
	}, [filteredData, selectedFilters, selectedObject]);

	// Calculate filtered data for allData prop

	// Calculate ISV owners
	const isvOwners = React.useMemo(() => {
		const owners = {};
		updatedData.forEach(obj => {
			if(obj['ISV_Opportunity_Owner__r.Name'] && !owners[obj['ISV_Opportunity_Owner__r.Name']]) {
				owners[obj['ISV_Opportunity_Owner__r.Name']] = 1;
			}
		});
		return Object.keys(owners);
	}, [updatedData]);

	if(isNullOrEmptyArray(updatedData)) {
		return (
			<PageBase
				removeMargins
				title={`${title} Report`}
				headerLinkText="Home Dashboard"
				headerLinkTarget={ROUTES.HOME}
			>
				<div style={{textAlign: 'center', marginTop: '100px', fontSize: '20px'}}>No results</div>
			</PageBase>
		)
	}

	return (
		<PageBase
			removeMargins
			title={`${title} Report`}
			headerLinkText="Home Dashboard"
			headerLinkTarget={ROUTES.HOME}
		>
			<PopupWin
				closeOnEscape={false}
				closeOnDocumentClick={false}
				visible={editOpportunity != null}
				onClose={() => setEditOpportunity(null)}
				title="Edit Opportunity"
				titleStyle={{fontSize: '18px', margin: '0 0 25px 0'}}
				contentStyle={{width: '100%', height: '90%', padding: 0}}
				popupStyle={{width: '90%', maxWidth: '1600px', height: '84%', maxHeight: '800px', overflow: 'none'}}
				footerStyle={{borderTop: '1px solid #D3E1ED'}}>
				{ editOpportunity && <EditOpportunity editMode={false} opportunity={editOpportunity} onClose={onOpportunitySaved} /> }
			</PopupWin>

			<div style={{margin: '0 30px'}}>
				{
					
					<div style={{display: 'flex', margin: '10px 0'}}>
						{selectedObject === 'opportunities' && <Filters 
							availableFilters={availableFilters(isvOwners)} 
							clearFilters={onClearFilters} 
							selectedFilters={selectedFilters} 
							onAddFilter={addFilter} 
							onRemoveFilter={removeFilter} 
						/>}
						<ListManager
						entityType={`dashboard-${title}`}
						displayFields={getDashboardFieldsByObject(selectedObject)}
						defaultFields={getDashboardFieldsByObject(selectedObject,true).map(f => f.field)}
						onColumnsChange={setListColumns}
					/>
					</div>
				}
					

				 <Datatable 
					gridHeight="calc(100vh - 185px)"
					columns={fieldsToUse}
					data={processedData}
					sortBy={sortBy}
					sortOrder={sortOrder}
					onRowClick={onRowClick}
					filters={filters}
					onFilterChange={handleFilterChange}
					filterOrder={filterOrder}
					uniqueValuesByColumn={uniqueValuesByColumn}
					allData={filteredAllData}
				/> 
			</div>
		</PageBase>
	)
};

export default DashboardReport;

const getRelevantLabelByField = (field, objectName) => {
	switch(objectName) {
		case "opportunities":
			return getOpportunityLabelByField(field);
		case "heatmaps":
			return getHeatmapLabelByField(field);
		case "marketing":
			return getMarketingLabelByField(field);
		case "funds":
			return getFundLabelByField(field);
	}
};

const getPathByObject = objectName => {
	switch(objectName) {
		case "opportunities":
			return "opportunity";
		case "heatmaps":
			return "heatmap";
		case "marketing":
			return "marketing";
		case "funds":
			return "fund"
	}
};

const prepareObjects = (objects, objectName) => {
	let columns;
	switch(objectName) {
		case "opportunities":
			columns = getOpportunityDashboardFields().map(f => f.field);
			break;
		case "heatmaps":
			columns = getHeatmapDashboardFields().map(f => f.field);
			break;
		case "marketing":
			columns = getMarketingDashboardFields().map(f => f.field);
			break;
		case "funds":
			columns = getFundDashboardFields().map(f => f.field);
			break;
	}

	function getNestedProperty(obj, path) {
		return path.split('.').reduce((acc, part) => acc && acc[part], obj);
	}

	function mapObjectsWithNestedFields(objects, columns) {
	return objects.map(item => {
		let result = { _original: item }; // Add original object
		columns.forEach(field => {
		const value = getNestedProperty(item, field);
		if (value !== undefined) {
			result[field] = value;
		}
		});
		return result;
	});
	}

		return mapObjectsWithNestedFields(objects, columns);
	};
